import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Player } from "@remotion/player";
import VideoComposition, { FPS } from "../remotion/VideoComposition";
import RemotionTimeline from "./RemotionTimeline";
import VideoHistory from "./VideoHistory";
import RegenerateImageModal from "./RegenerateImageModal";
import ImageHistoryModal from "./ImageHistoryModal";
import AnimationProgressBar from "./AnimationProgressBar";
import SoundEffectSearch from "./SoundEffectSearch";
import SoundEffectClipEditor from "./SoundEffectClipEditor";
import PublishDialog from "./PublishDialog";
import ThumbnailActions from "Redux/ThumbnailRedux";
import { parseSubtitles } from "../utils/subtitles";

const hexToRgba = (hex, opacityPercent) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "transparent";
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const a = Math.min(100, Math.max(0, opacityPercent)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const getCompositionDimensions = (aspectRatio) => {
  switch (aspectRatio) {
    case "9:16":
      return { width: 1080, height: 1920 };
    case "1:1":
      return { width: 1080, height: 1080 };
    case "16:9":
    default:
      return { width: 1920, height: 1080 };
  }
};

const getPlayerContainerStyles = (aspectRatio) => {
  switch (aspectRatio) {
    case "9:16":
      return {
        containerClass: "w-full max-w-[480px]",
        wrapperClass: "flex justify-center",
      };
    case "1:1":
      return {
        containerClass: "w-full max-w-[640px]",
        wrapperClass: "flex justify-center",
      };
    case "16:9":
    default:
      return {
        containerClass: "w-full",
        wrapperClass: "",
      };
  }
};

export default function VideoPreviewPlayer({
  audioUrl,
  images = [],
  savedSegments = [],
  aspectRatio = "16:9",
  onSegmentsChange,
  onExportVideo,
  isExporting = false,
  isSavingDraft = false,
  videoHistory = [],
  onRefreshHistory,
  subtitleData = "",
  subtitlesEnabled = false,
  onSubtitlesChange,
  subtitleStyle = "default",
  onSubtitleStyleChange,
  audioId = null,
  animateVisuals = false,
  animationStats = null,
  onAnimationStatsChange,
  soundEffectClips = [],
  onSoundEffectClipsChange,
  showWatermark = true,
  initialSubtitleSettings = null,
  onSubtitleSettingsChange,
  videoId = null,
  videoUrl = null,
  topic = "",
  scriptId = null,
}) {
  const dispatch = useDispatch();
  const playerRef = useRef(null);
  const audioRef = useRef(null);
  const sfxAudioRefs = useRef({});

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [segments, setSegments] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevImagesRef = useRef(images);

  // Image regeneration state
  const [regenerateModal, setRegenerateModal] = useState({
    isOpen: false,
    segment: null,
  });
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    segment: null,
  });
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSelectingFromHistory, setIsSelectingFromHistory] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  // Subtitle customization state
  const defaultSubtitleSettings = {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 48,
    color: "#ffffff",
    position: "bottom-center",
    backgroundColorEnabled: false,
    backgroundColor: "#000000",
    backgroundColorOpacity: 50,
  };
  const [subtitleSettings, setSubtitleSettings] = useState(
    initialSubtitleSettings && Object.keys(initialSubtitleSettings).length > 0
      ? { ...defaultSubtitleSettings, ...initialSubtitleSettings }
      : defaultSubtitleSettings,
  );

  // Animation state
  const [animatingSegments, setAnimatingSegments] = useState(new Set());

  // Sound effects state
  const [sfxClips, setSfxClips] = useState(soundEffectClips);
  const [selectedSfxClip, setSelectedSfxClip] = useState(null);
  const [showSfxSearch, setShowSfxSearch] = useState(false);
  const sfxClipIdCounter = useRef(
    soundEffectClips.length > 0
      ? Math.max(...soundEffectClips.map((c) => c.id || 0)) + 1
      : 1,
  );

  useEffect(() => {
    setSfxClips(soundEffectClips);
  }, [soundEffectClips]);

  // Notify parent when subtitle settings change (for draft saving)
  useEffect(() => {
    onSubtitleSettingsChange?.(subtitleSettings);
  }, [subtitleSettings]);

  // Parse subtitles from subtitle data (auto-detects SRT or ASS format)
  const subtitles = useMemo(() => {
    if (!subtitleData) return [];
    return parseSubtitles(subtitleData);
  }, [subtitleData]);

  // Composition dimensions
  const compositionDimensions = useMemo(
    () => getCompositionDimensions(aspectRatio),
    [aspectRatio],
  );
  const playerStyles = useMemo(
    () => getPlayerContainerStyles(aspectRatio),
    [aspectRatio],
  );

  // Duration in frames for Remotion
  const durationInFrames = useMemo(() => {
    if (duration <= 0) return 1;
    return Math.ceil(duration * FPS);
  }, [duration]);

  // Reset when images array changes
  useEffect(() => {
    if (prevImagesRef.current !== images) {
      setIsInitialized(false);
      prevImagesRef.current = images;
    }
  }, [images]);

  // Initialize segments when duration and images are available
  useEffect(() => {
    if (duration > 0 && !isInitialized) {
      if (savedSegments.length > 0) {
        setSegments(savedSegments);
      } else if (images.length > 0) {
        const hasTimestamps = images.some(
          (img) => img.start_time !== undefined,
        );

        const initialSegments = images.map((img, index) => {
          if (hasTimestamps && img.start_time !== undefined) {
            return {
              id: img.id || `segment-${index}`,
              thumbnail_image_id: img.id,
              image_url: img.image_url,
              prompt: img.prompt,
              startTime: img.start_time,
              endTime: img.end_time,
              animation_prompt: img.animation_prompt || "",
              animation_status: img.animation_status,
              animated_video_url: img.animated_video_url,
              animate_enabled: img.animate_enabled || false,
            };
          } else {
            const segmentDuration = duration / images.length;
            return {
              id: img.id || `segment-${index}`,
              thumbnail_image_id: img.id,
              image_url: img.image_url,
              prompt: img.prompt,
              startTime: index * segmentDuration,
              endTime: (index + 1) * segmentDuration,
              animation_prompt: img.animation_prompt || "",
              animation_status: img.animation_status,
              animated_video_url: img.animated_video_url,
              animate_enabled: img.animate_enabled || false,
            };
          }
        });
        setSegments(initialSegments);
      }
      setIsInitialized(true);
    }
  }, [duration, images, savedSegments, isInitialized]);

  // Find current segment index
  const activeSegmentIndex = useMemo(() => {
    const idx = segments.findIndex(
      (seg) => currentTime >= seg.startTime && currentTime < seg.endTime,
    );
    return idx >= 0 ? idx : segments.length > 0 ? segments.length - 1 : -1;
  }, [segments, currentTime]);

  const activeSegment = segments[activeSegmentIndex] || null;

  // Audio duration detection - use standalone Audio object for reliability
  useEffect(() => {
    if (!audioUrl) return;

    // Try getting duration from a standalone Audio object (most reliable)
    const probe = new Audio();
    probe.preload = "metadata";

    const onMeta = () => {
      if (probe.duration && isFinite(probe.duration) && probe.duration > 0) {
        setDuration(probe.duration);
      }
    };

    probe.addEventListener("loadedmetadata", onMeta);
    probe.addEventListener("durationchange", onMeta);
    probe.src = audioUrl;

    return () => {
      probe.removeEventListener("loadedmetadata", onMeta);
      probe.removeEventListener("durationchange", onMeta);
      probe.pause();
      probe.src = "";
    };
  }, [audioUrl]);

  // Fallback: compute duration from saved segments if audio hasn't loaded yet
  useEffect(() => {
    if (duration > 0) return;
    if (savedSegments.length > 0) {
      const maxEnd = Math.max(
        ...savedSegments.map((s) => s.endTime || s.end_time || 0),
      );
      if (maxEnd > 0) setDuration(maxEnd);
    } else if (
      images.length > 0 &&
      images.some((img) => img.end_time !== undefined)
    ) {
      const maxEnd = Math.max(...images.map((img) => img.end_time || 0));
      if (maxEnd > 0) setDuration(maxEnd);
    }
  }, [duration, savedSegments, images]);

  // Also set ref duration when audio element loads (for playback sync)
  const handleLoadedMetadata = useCallback(() => {
    if (
      audioRef.current &&
      audioRef.current.duration &&
      isFinite(audioRef.current.duration)
    ) {
      setDuration((prev) => (prev > 0 ? prev : audioRef.current.duration));
    }
  }, []);

  // ============ Remotion Player Controls + Audio Sync ============

  const handlePlayerTimeUpdate = useCallback((e) => {
    const time = e.detail.frame / FPS;
    setCurrentTime(time);

    // Keep HTML audio in sync with Remotion Player
    if (
      audioRef.current &&
      Math.abs(audioRef.current.currentTime - time) > 0.3
    ) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const handlePlayerPlay = useCallback(() => {
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      audioRef.current.play().catch(() => {});
    }
  }, [isMuted]);

  const handlePlayerPause = useCallback(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Pause all SFX
    Object.values(sfxAudioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) audio.pause();
    });
  }, []);

  const handlePlayerEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    Object.values(sfxAudioRefs.current).forEach((audio) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }, []);

  // Attach Player event listeners
  // Must depend on segments.length and duration so this re-runs after the
  // <Player> conditionally mounts (it only renders when duration > 0 && segments.length > 0).
  useEffect(() => {
    const player = playerRef.current;
    if (!player) {
      console.log(
        "[VideoPreviewPlayer] Player ref not available yet, skipping event listener setup",
      );
      return;
    }

    console.log("[VideoPreviewPlayer] Attaching event listeners to Player ref");

    player.addEventListener("timeupdate", handlePlayerTimeUpdate);
    player.addEventListener("play", handlePlayerPlay);
    player.addEventListener("pause", handlePlayerPause);
    player.addEventListener("ended", handlePlayerEnded);

    return () => {
      player.removeEventListener("timeupdate", handlePlayerTimeUpdate);
      player.removeEventListener("play", handlePlayerPlay);
      player.removeEventListener("pause", handlePlayerPause);
      player.removeEventListener("ended", handlePlayerEnded);
    };
  }, [
    handlePlayerTimeUpdate,
    handlePlayerPlay,
    handlePlayerPause,
    handlePlayerEnded,
    durationInFrames,
    segments.length,
    duration,
  ]);

  // ============ SFX Audio Playback ============

  useEffect(() => {
    if (!isPlaying) {
      Object.values(sfxAudioRefs.current).forEach((audio) => {
        if (audio && !audio.paused) audio.pause();
      });
      return;
    }

    sfxClips.forEach((clip) => {
      const clipStart = clip.start_time;
      const clipEnd = clip.end_time;
      const sfxUrl = clip.asset?.audio_url || clip.asset?.preview_url;
      if (!sfxUrl) return;

      const isInRange = currentTime >= clipStart && currentTime < clipEnd;

      if (!sfxAudioRefs.current[clip.id]) {
        const audio = new Audio(sfxUrl);
        audio.loop = clip.loop_enabled;
        audio.volume = (clip.volume || 50) / 100;
        sfxAudioRefs.current[clip.id] = audio;
      }

      const audio = sfxAudioRefs.current[clip.id];
      audio.volume = (clip.volume || 50) / 100;
      audio.loop = clip.loop_enabled;
      audio.muted = isMuted;

      if (isInRange) {
        if (audio.paused) {
          const clipElapsed = currentTime - clipStart;
          const assetDuration = clip.asset?.duration || audio.duration || 1;
          audio.currentTime = clip.loop_enabled
            ? clipElapsed % assetDuration
            : Math.min(clipElapsed, assetDuration);
          audio.play().catch(() => {});
        }
      } else {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      }
    });
  }, [currentTime, isPlaying, sfxClips, isMuted]);

  // Cleanup SFX audio elements
  useEffect(() => {
    const currentClipIds = new Set(sfxClips.map((c) => c.id));
    Object.keys(sfxAudioRefs.current).forEach((id) => {
      if (!currentClipIds.has(Number(id))) {
        const audio = sfxAudioRefs.current[id];
        if (audio) {
          audio.pause();
          audio.src = "";
        }
        delete sfxAudioRefs.current[id];
      }
    });
  }, [sfxClips]);

  useEffect(() => {
    return () => {
      Object.values(sfxAudioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = "";
        }
      });
      sfxAudioRefs.current = {};
    };
  }, []);

  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      console.log("[VideoPreviewPlayer] togglePlay: playerRef.current is null");
      return;
    }
    console.log("[VideoPreviewPlayer] togglePlay: calling player.toggle()");
    player.toggle();
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      if (audioRef.current) {
        audioRef.current.muted = next;
      }
      Object.values(sfxAudioRefs.current).forEach((audio) => {
        if (audio) audio.muted = next;
      });
      return next;
    });
  }, []);

  const handleSeek = useCallback(
    (time) => {
      const player = playerRef.current;
      if (!player) return;
      const frame = Math.round(time * FPS);
      player.seekTo(Math.min(frame, durationInFrames - 1));
      setCurrentTime(time);

      // Sync audio element
      if (audioRef.current) {
        audioRef.current.currentTime = time;
      }
      // Reset SFX on seek
      Object.values(sfxAudioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    },
    [durationInFrames],
  );

  // ============ Segment Editing Handlers ============

  const handleSegmentsChange = (newSegments) => {
    setSegments(newSegments);
    onSegmentsChange?.(newSegments);
  };

  const updateSegmentImage = (segmentId, newImageUrl, newPrompt) => {
    const newSegments = segments.map((seg) =>
      seg.id === segmentId
        ? { ...seg, image_url: newImageUrl, prompt: newPrompt }
        : seg,
    );
    setSegments(newSegments);
    onSegmentsChange?.(newSegments);
  };

  const handleRegenerateImage = (segment) => {
    setRegenerateModal({ isOpen: true, segment });
  };

  const handleRegenerateSubmit = (segmentId, prompt) => {
    setIsRegenerating(true);

    dispatch(
      ThumbnailActions.thumbnailRegenerateRequest(
        segmentId,
        prompt,
        (regenData, regenError) => {
          if (regenError) {
            console.error("Failed to regenerate image:", regenError);
            alert(regenError || "Failed to regenerate image");
            setIsRegenerating(false);
            return;
          }

          dispatch(
            ThumbnailActions.thumbnailPollRequest(
              segmentId,
              3000,
              (result, pollError) => {
                setIsRegenerating(false);

                if (pollError) {
                  console.error("Failed to regenerate image:", pollError);
                  alert(pollError || "Failed to regenerate image");
                  return;
                }

                updateSegmentImage(segmentId, result.image_url, result.prompt);
                setRegenerateModal({ isOpen: false, segment: null });
              },
            ),
          );
        },
      ),
    );
  };

  const handleViewImageHistory = (segment) => {
    setHistoryModal({ isOpen: true, segment });
  };

  const handleSelectFromHistory = (segmentId, historyIndex) => {
    setIsSelectingFromHistory(true);

    dispatch(
      ThumbnailActions.thumbnailSelectHistoryRequest(
        segmentId,
        historyIndex,
        (result, error) => {
          setIsSelectingFromHistory(false);

          if (error) {
            console.error("Failed to select from history:", error);
            alert(error || "Failed to select image from history");
            return;
          }

          updateSegmentImage(segmentId, result.image_url, result.prompt);
          setHistoryModal({ isOpen: false, segment: null });
        },
      ),
    );
  };

  const updateSegmentAnimation = (segmentId, animationData) => {
    const newSegments = segments.map((seg) =>
      seg.id === segmentId ? { ...seg, ...animationData } : seg,
    );
    setSegments(newSegments);
    onSegmentsChange?.(newSegments);
  };

  // ============ Sound Effects Handlers ============

  const handleAddSoundEffect = (sfx) => {
    const newClip = {
      id: sfxClipIdCounter.current++,
      start_time: currentTime,
      end_time: Math.min(currentTime + sfx.duration, duration),
      loop_enabled: true,
      volume: 50,
      layer_index: 0,
      asset: {
        provider_sound_id: sfx.provider_sound_id,
        name: sfx.name,
        preview_url: sfx.preview_url,
        audio_url: sfx.audio_url,
        duration: sfx.duration,
        category: sfx.category,
      },
    };

    const newClips = [...sfxClips, newClip];
    setSfxClips(newClips);
    onSoundEffectClipsChange?.(newClips);
    setSelectedSfxClip(newClip);
    setShowSfxSearch(false);
  };

  const handleSfxClipSelect = (clip) => {
    setSelectedSfxClip(clip);
  };

  const handleSfxClipUpdate = (clipId, updates) => {
    const newClips = sfxClips.map((clip) =>
      clip.id === clipId ? { ...clip, ...updates } : clip,
    );
    setSfxClips(newClips);
    onSoundEffectClipsChange?.(newClips);

    if (selectedSfxClip?.id === clipId) {
      setSelectedSfxClip({ ...selectedSfxClip, ...updates });
    }
  };

  const handleSfxClipDelete = (clipId) => {
    const newClips = sfxClips.filter((clip) => clip.id !== clipId);
    setSfxClips(newClips);
    onSoundEffectClipsChange?.(newClips);

    if (selectedSfxClip?.id === clipId) {
      setSelectedSfxClip(null);
    }
  };

  // ============ Animation Handlers ============

  const handleGenerateAnimation = (segment) => {
    if (!segment?.id || animatingSegments.has(segment.id)) return;

    const forceReanimate =
      segment.animation_status === "completed" || !!segment.animated_video_url;

    setAnimatingSegments((prev) => new Set([...prev, segment.id]));
    updateSegmentAnimation(segment.id, { animation_status: "processing" });

    dispatch(
      ThumbnailActions.animationGenerateRequest(
        segment.id,
        null,
        forceReanimate,
        (genData, genError) => {
          if (genError) {
            console.error("Failed to generate animation:", genError);
            updateSegmentAnimation(segment.id, {
              animation_status: "failed",
              animation_error_message: genError,
            });
            setAnimatingSegments((prev) => {
              const next = new Set(prev);
              next.delete(segment.id);
              return next;
            });
            return;
          }

          dispatch(
            ThumbnailActions.animationPollRequest(
              segment.id,
              3000,
              (result, pollError) => {
                setAnimatingSegments((prev) => {
                  const next = new Set(prev);
                  next.delete(segment.id);
                  return next;
                });

                if (pollError) {
                  console.error("Failed to generate animation:", pollError);
                  updateSegmentAnimation(segment.id, {
                    animation_status: "failed",
                    animation_error_message: pollError,
                  });
                  return;
                }

                updateSegmentAnimation(segment.id, {
                  animation_status: result.animation_status,
                  animated_video_url: result.animated_video_url,
                  animation_prompt: result.animation_prompt,
                  animation_error_message: null,
                });
              },
            ),
          );
        },
      ),
    );
  };

  const handleRegenerateAnimation = (segment) => {
    if (!segment?.id || animatingSegments.has(segment.id)) return;

    setAnimatingSegments((prev) => new Set([...prev, segment.id]));
    updateSegmentAnimation(segment.id, { animation_status: "processing" });

    dispatch(
      ThumbnailActions.animationRegenerateRequest(
        segment.id,
        (regenData, regenError) => {
          if (regenError) {
            console.error("Failed to regenerate animation:", regenError);
            updateSegmentAnimation(segment.id, {
              animation_status: "failed",
              animation_error_message: regenError,
            });
            setAnimatingSegments((prev) => {
              const next = new Set(prev);
              next.delete(segment.id);
              return next;
            });
            return;
          }

          dispatch(
            ThumbnailActions.animationPollRequest(
              segment.id,
              3000,
              (result, pollError) => {
                setAnimatingSegments((prev) => {
                  const next = new Set(prev);
                  next.delete(segment.id);
                  return next;
                });

                if (pollError) {
                  console.error("Failed to regenerate animation:", pollError);
                  updateSegmentAnimation(segment.id, {
                    animation_status: "failed",
                    animation_error_message: pollError,
                  });
                  return;
                }

                updateSegmentAnimation(segment.id, {
                  animation_status: result.animation_status,
                  animated_video_url: result.animated_video_url,
                  animation_prompt: result.animation_prompt,
                  animation_error_message: null,
                });
              },
            ),
          );
        },
      ),
    );
  };

  const handleSkipAnimation = (segment) => {
    if (!segment?.id) return;

    dispatch(
      ThumbnailActions.animationSkipRequest(segment.id, (data, error) => {
        if (error) {
          console.error("Failed to skip animation:", error);
          alert(error || "Failed to skip animation");
          return;
        }

        updateSegmentAnimation(segment.id, {
          animation_status: "skipped",
          animation_error_message: null,
        });
      }),
    );
  };

  const handleRetryFailedAnimations = async (failedThumbnails) => {
    for (const thumbnail of failedThumbnails) {
      const segment = segments.find((s) => s.id === thumbnail.id);
      if (segment) {
        handleRegenerateAnimation(segment);
      }
    }
  };

  // ============ Remotion Composition inputProps (visuals only) ============

  const inputProps = useMemo(
    () => ({
      segments,
      subtitles,
      subtitlesEnabled,
      subtitleSettings,
      showWatermark,
    }),
    [segments, subtitles, subtitlesEnabled, showWatermark, subtitleSettings],
  );

  const renderPlayerLoading = useCallback(
    ({ width, height }) => (
      <div
        style={{
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8fafc",
        }}
      >
        <div style={{ color: "#6b7280", fontSize: 14 }}>Loading preview...</div>
      </div>
    ),
    [],
  );

  const renderPlayerError = useCallback(
    ({ error }) => (
      <div
        style={{
          padding: 20,
          color: "#ef4444",
          backgroundColor: "#fef2f2",
          borderRadius: 12,
          textAlign: "center",
        }}
      >
        <p style={{ fontWeight: 600, marginBottom: 8 }}>Preview Error</p>
        <p style={{ fontSize: 13, color: "#6b7280" }}>{error.message}</p>
      </div>
    ),
    [],
  );

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Audio element for playback synced with Remotion Player */}
      <audio
        key={audioUrl}
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={handleLoadedMetadata}
        preload="auto"
      />

      {/* Remotion Player Preview */}
      <div className={playerStyles.wrapperClass}>
        <div
          className={`relative bg-gray-100 rounded-xl overflow-hidden border border-gray-200 ${playerStyles.containerClass}`}
        >
          {duration > 0 && segments.length > 0 ? (
            <>
              <Player
                ref={playerRef}
                component={VideoComposition}
                inputProps={inputProps}
                durationInFrames={durationInFrames}
                compositionWidth={compositionDimensions.width}
                compositionHeight={compositionDimensions.height}
                fps={FPS}
                style={{ width: "100%" }}
                clickToPlay={true}
                doubleClickToFullscreen={true}
                spaceKeyToPlayOrPause={true}
                renderLoading={renderPlayerLoading}
                errorFallback={renderPlayerError}
                acknowledgeRemotionLicense
              />
              {/* Scene indicator overlay */}
              <div className="absolute bottom-4 right-4 px-2 py-1 bg-white/80 text-gray-700 text-sm rounded border border-gray-200 pointer-events-none z-10 shadow-sm">
                Scene {activeSegmentIndex + 1} of {segments.length}
              </div>
            </>
          ) : (
            <div className="aspect-video flex items-center justify-center">
              <div className="text-gray-400 flex items-center gap-2">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm">Loading preview...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Playback Controls */}
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            disabled={duration <= 0}
            className="w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            onClick={toggleMute}
            disabled={duration <= 0}
            title={isMuted ? "Unmute" : "Mute"}
            className="w-11 h-11 text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            {isMuted ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>

          {/* Progress bar */}
          <div className="flex-1 flex flex-col gap-1">
            <div
              className="relative h-3 sm:h-2 bg-gray-200 rounded-full cursor-pointer group"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                handleSeek(percent * duration);
              }}
            >
              <div
                className="absolute top-0 left-0 h-full bg-primary-500 rounded-full transition-all"
                style={{
                  width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-primary-500 rounded-full border-2 border-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-mono">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              {activeSegment && (
                <span className="text-xs text-gray-400">
                  Segment {activeSegmentIndex + 1} (
                  {formatTime(activeSegment.startTime)} -{" "}
                  {formatTime(activeSegment.endTime)})
                </span>
              )}
            </div>
          </div>

          {/* Auto-save indicator */}
          {isSavingDraft && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg
                className="w-3.5 h-3.5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Saving...
            </span>
          )}
        </div>
      </div>

      {/* Animation Progress Bar */}
      {animateVisuals && animationStats && animationStats.total > 0 && (
        <AnimationProgressBar
          stats={animationStats}
          thumbnails={segments}
          onRetryFailed={handleRetryFailedAnimations}
        />
      )}

      {/* Remotion-style Multi-Track Timeline */}
      {duration > 0 && segments.length > 0 && (
        <RemotionTimeline
          duration={duration}
          segments={segments}
          onSegmentsChange={handleSegmentsChange}
          currentTime={currentTime}
          onSeek={handleSeek}
          isPlaying={isPlaying}
          onRegenerateImage={handleRegenerateImage}
          onViewImageHistory={handleViewImageHistory}
          onRegenerateAnimation={handleRegenerateAnimation}
          onSkipAnimation={handleSkipAnimation}
          onGenerateAnimation={handleGenerateAnimation}
          animateVisuals={animateVisuals}
          audioUrl={audioUrl}
          sfxClips={sfxClips}
          onSfxClipSelect={handleSfxClipSelect}
          onSfxClipUpdate={handleSfxClipUpdate}
          onSfxClipDelete={handleSfxClipDelete}
          selectedSfxClipId={selectedSfxClip?.id}
        />
      )}

      {/* Sound Effects Controls */}
      {duration > 0 && (
        <div className="flex gap-4">
          <div className="flex-1">
            {showSfxSearch ? (
              <div className="relative">
                <button
                  onClick={() => setShowSfxSearch(false)}
                  className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-300"
                >
                  ✕
                </button>
                <SoundEffectSearch
                  onSelect={handleAddSoundEffect}
                  disabled={isExporting}
                />
              </div>
            ) : (
              <button
                onClick={() => setShowSfxSearch(true)}
                disabled={isExporting}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
                <span className="font-medium">Add Sound Effect</span>
              </button>
            )}
          </div>

          {selectedSfxClip && (
            <div className="w-full sm:w-80 flex-shrink-0">
              <SoundEffectClipEditor
                clip={selectedSfxClip}
                onUpdate={handleSfxClipUpdate}
                onDelete={handleSfxClipDelete}
                onClose={() => setSelectedSfxClip(null)}
                disabled={isExporting}
              />
            </div>
          )}
        </div>
      )}

      {/* Regenerate Image Modal */}
      <RegenerateImageModal
        isOpen={regenerateModal.isOpen}
        onClose={() => setRegenerateModal({ isOpen: false, segment: null })}
        segment={regenerateModal.segment}
        onRegenerate={handleRegenerateSubmit}
        isRegenerating={isRegenerating}
      />

      {/* Image History Modal */}
      <ImageHistoryModal
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false, segment: null })}
        segment={historyModal.segment}
        onSelectImage={handleSelectFromHistory}
        isSelecting={isSelectingFromHistory}
      />

      {/* Video History */}
      <VideoHistory videos={videoHistory} onRefresh={onRefreshHistory} />

      {/* Export Settings */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-20">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Subtitles
                </h3>
                <p className="text-[11px] text-gray-400">
                  Burned-in captions on exported video
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={subtitlesEnabled}
                onChange={(e) => onSubtitlesChange?.(e.target.checked)}
                disabled={isExporting}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
            </label>
          </div>
        </div>

        {/* Subtitle Customization */}
        {subtitlesEnabled && (
          <div className="p-5">
            <div className="grid grid-cols-2 gap-5">
              {/* Left Column — Typography */}
              <div className="space-y-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <svg
                    className="w-3.5 h-3.5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6h16M4 12h8m-8 6h16"
                    />
                  </svg>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Typography
                  </span>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Font
                  </label>
                  <select
                    value={subtitleSettings.fontFamily}
                    onChange={(e) =>
                      setSubtitleSettings((prev) => ({
                        ...prev,
                        fontFamily: e.target.value,
                      }))
                    }
                    disabled={isExporting}
                    className="w-full px-3 py-2 text-base border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-shadow disabled:opacity-50"
                  >
                    <option value='-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'>
                      System Default
                    </option>
                    <option value="Arial, Helvetica, sans-serif">Arial</option>
                    <option value="Verdana, Geneva, sans-serif">Verdana</option>
                    <option value='"Trebuchet MS", Helvetica, sans-serif'>
                      Trebuchet MS
                    </option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value='"Times New Roman", Times, serif'>
                      Times New Roman
                    </option>
                    <option value='"Courier New", Courier, monospace'>
                      Courier New
                    </option>
                    <option value="Impact, Charcoal, sans-serif">Impact</option>
                    <option value='"Comic Sans MS", cursive, sans-serif'>
                      Comic Sans MS
                    </option>
                  </select>
                </div>

                {/* Font Size */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Size
                    </label>
                    <span className="text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-0.5 rounded-md">
                      {subtitleSettings.fontSize}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={25}
                    max={80}
                    step={1}
                    value={subtitleSettings.fontSize}
                    onChange={(e) =>
                      setSubtitleSettings((prev) => ({
                        ...prev,
                        fontSize: Number(e.target.value),
                      }))
                    }
                    disabled={isExporting}
                    className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-500 disabled:opacity-50"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>Small</span>
                    <span>Large</span>
                  </div>
                </div>
              </div>

              {/* Right Column — Appearance */}
              <div className="space-y-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <svg
                    className="w-3.5 h-3.5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                    />
                  </svg>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Appearance
                  </span>
                </div>

                {/* Font Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Color
                  </label>
                  <div className="flex items-center gap-1.5">
                    {[
                      { label: "White", value: "#ffffff" },
                      { label: "Yellow", value: "#facc15" },
                      { label: "Cyan", value: "#22d3ee" },
                      { label: "Green", value: "#4ade80" },
                      { label: "Orange", value: "#fb923c" },
                      { label: "Pink", value: "#f472b6" },
                    ].map((swatch) => (
                      <button
                        key={swatch.value}
                        onClick={() =>
                          setSubtitleSettings((prev) => ({
                            ...prev,
                            color: swatch.value,
                          }))
                        }
                        disabled={isExporting}
                        title={swatch.label}
                        className={`w-7 h-7 rounded-lg transition-all disabled:opacity-50 ${
                          subtitleSettings.color === swatch.value
                            ? "ring-2 ring-primary-500 ring-offset-1 scale-105"
                            : "ring-1 ring-gray-200 hover:ring-gray-300"
                        }`}
                        style={{ backgroundColor: swatch.value }}
                      />
                    ))}
                    <div className="w-7 h-7 rounded-lg overflow-hidden ring-1 ring-gray-200 relative">
                      <input
                        type="color"
                        value={subtitleSettings.color}
                        onChange={(e) =>
                          setSubtitleSettings((prev) => ({
                            ...prev,
                            color: e.target.value,
                          }))
                        }
                        disabled={isExporting}
                        className="absolute inset-0 w-full h-full cursor-pointer opacity-0 disabled:opacity-50"
                        title="Custom color"
                      />
                      <div className="w-full h-full bg-gradient-to-br from-red-400 via-green-400 to-blue-400 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white drop-shadow"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Position */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Position
                  </label>
                  <div className="inline-grid grid-cols-3 gap-[3px] p-1.5 bg-gray-100 rounded-lg">
                    {[
                      { value: "top-left", label: "↖" },
                      { value: "top-center", label: "↑" },
                      { value: "top-right", label: "↗" },
                      { value: "center-left", label: "←" },
                      { value: "center-center", label: "•" },
                      { value: "center-right", label: "→" },
                      { value: "bottom-left", label: "↙" },
                      { value: "bottom-center", label: "↓" },
                      { value: "bottom-right", label: "↘" },
                    ].map((pos) => (
                      <button
                        key={pos.value}
                        onClick={() =>
                          setSubtitleSettings((prev) => ({
                            ...prev,
                            position: pos.value,
                          }))
                        }
                        disabled={isExporting}
                        title={pos.value.replace("-", " ")}
                        className={`w-7 h-7 rounded-md text-xs flex items-center justify-center transition-all disabled:opacity-50 ${
                          subtitleSettings.position === pos.value
                            ? "bg-primary-500 text-white shadow-sm font-bold"
                            : "bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 shadow-sm"
                        }`}
                      >
                        {pos.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Color */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                    <input
                      type="checkbox"
                      checked={subtitleSettings.backgroundColorEnabled ?? false}
                      onChange={(e) =>
                        setSubtitleSettings((prev) => ({
                          ...prev,
                          backgroundColorEnabled: e.target.checked,
                        }))
                      }
                      disabled={isExporting}
                      className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-300"
                    />
                    <span className="text-xs font-medium text-gray-600">Background color</span>
                  </label>
                  {subtitleSettings.backgroundColorEnabled && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="color"
                        value={subtitleSettings.backgroundColor || "#000000"}
                        onChange={(e) =>
                          setSubtitleSettings((prev) => ({
                            ...prev,
                            backgroundColor: e.target.value,
                          }))
                        }
                        disabled={isExporting}
                        className="w-7 h-7 rounded-md border border-gray-200 cursor-pointer p-0.5 bg-white disabled:opacity-50"
                      />
                      <input
                        type="text"
                        value={subtitleSettings.backgroundColor || "#000000"}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (/^#[0-9a-fA-F]{0,6}$/.test(v) || v === "") {
                            setSubtitleSettings((prev) => ({
                              ...prev,
                              backgroundColor: v || "#000000",
                            }));
                          }
                        }}
                        placeholder="#000000"
                        disabled={isExporting}
                        className="w-20 px-2 py-1 text-xs font-mono border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 disabled:opacity-50"
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={subtitleSettings.backgroundColorOpacity ?? 50}
                        onChange={(e) => {
                          const v = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                          setSubtitleSettings((prev) => ({
                            ...prev,
                            backgroundColorOpacity: v,
                          }));
                        }}
                        disabled={isExporting}
                        className="w-12 px-1.5 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 disabled:opacity-50"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="mt-4 rounded-xl overflow-hidden border border-gray-200">
              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-300"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-300"></div>
                <div className="w-2 h-2 rounded-full bg-green-300"></div>
                <span className="text-[10px] text-gray-400 ml-1.5">
                  Preview
                </span>
              </div>
              <div className="bg-gray-800 relative" style={{ height: 80 }}>
                <div
                  className="absolute inset-0 flex flex-col"
                  style={{
                    justifyContent: subtitleSettings.position?.startsWith("top")
                      ? "flex-start"
                      : subtitleSettings.position?.startsWith("center")
                        ? "center"
                        : "flex-end",
                    alignItems: subtitleSettings.position?.endsWith("left")
                      ? "flex-start"
                      : subtitleSettings.position?.endsWith("right")
                        ? "flex-end"
                        : "center",
                    padding: 10,
                  }}
                >
                  <p
                    style={{
                      fontFamily: subtitleSettings.fontFamily,
                      fontSize: Math.min(subtitleSettings.fontSize, 40),
                      color: subtitleSettings.color,
                      fontWeight: 600,
                      textShadow: "1px 1px 3px rgba(0,0,0,0.9)",
                      ...(subtitleSettings.backgroundColorEnabled
                        ? {
                            backgroundColor: hexToRgba(
                              subtitleSettings.backgroundColor || "#000000",
                              subtitleSettings.backgroundColorOpacity ?? 50
                            ),
                          }
                        : {}),
                      padding: "3px 10px",
                      borderRadius: 5,
                      maxWidth: "85%",
                      textAlign: "center",
                      lineHeight: 1.4,
                    }}
                  >
                    Sample subtitle text
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Export Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {isSavingDraft ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin text-yellow-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving draft...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Timeline ready for export
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPublishDialog(true)}
            className="px-5 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
            </svg>
            Publish
          </button>
          <button
            onClick={() =>
              onExportVideo({
                subtitlesEnabled,
                subtitleStyle,
                subtitleSettings,
              })
            }
            disabled={isExporting || !audioUrl || segments.length === 0}
            className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm"
          >
            {isExporting ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Export Final Video
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      <PublishDialog
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        videoId={videoId}
        videoUrl={videoUrl}
        topic={topic}
        scriptId={scriptId}
      />
    </div>
  );
}
